import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  TextField,
} from "@mui/material";
import SaveAltOutlinedIcon from '@mui/icons-material/SaveAltOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import { getReportDetails } from "../../features/reportDetails/reportDetailsAction";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { getUsersReportDetails } from "../../features/usersGraphDetails/usersGraphDetailsAction";
import CustomDateRangePopup from "../Common/DatePicker";
import ExportToExcel from "../Common/ExportToExcel";

const getCurrentMonthYear = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${now.getFullYear()}-${month}`;
};

const ReportDetail = () => {
  const [userGraphView, setUserGraphView] = useState("monthly");
  const [reportExportData, setReportExportData] = useState([])
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [customViewRef, setCustomViewRef] = useState("");
  const [reportDetails, setReportDetails] = useState({});
  const [userGraphData, setUserGraphData] = useState({});
  const [subscriptionView, setSubscriptionView] = useState(getCurrentMonthYear());
  const location = useLocation();
  const inputRef = useRef(false);
  const hasFetched = useRef(false);
  const dispatch = useDispatch();
  const { tenant } = location.state || {};

  const formatDate = (date) => date.toISOString().split("T")[0];

  const today = formatDate(new Date());
  const firstOfMonth = formatDate(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);

  const subscriptionData = [
    { month: "Jan", amount: 150 },
    { month: "Feb", amount: 200 },
    { month: "Mar", amount: 250 },
    { month: "Apr", amount: 400 },
    { month: "May", amount: 350 },
    { month: "June", amount: 300 },
    { month: "July", amount: 270 },
    { month: "Aug", amount: 360 },
    { month: "Sep", amount: 420 },
    { month: "Oct", amount: 310 },
    { month: "Nov", amount: 370 },
    { month: "Dec", amount: 450 }
  ];
  const getReportDetail = useCallback(async (id) => {
    dispatch(getReportDetails({ tenantId: id })).then((res) => {
      if (res?.payload) {
        setReportDetails(res?.payload?.data);
      }
    });
  }, [dispatch]);

  const getUsersGraphReportDetail = useCallback(async (id, type, start = "", end = "") => {

    dispatch(getUsersReportDetails({ tenantId: id, type, start, end })).then((res) => {
      if (res?.payload) {
        let constructGraphData = []
        if (type === "current-month") {
          constructGraphData = res?.payload?.data?.currentMonthReport?.map((item) => {
            return {
              xAxis: item.dateNumber,
              total: item.total,
              new: item.new
            }
          });
          setReportExportData(res?.payload?.data?.currentMonthReport)
          setUserGraphData(constructGraphData);
        } else if (type === "current-week") {
          constructGraphData = res?.payload?.data?.currentWeekReport?.map((item) => {
            return {
              xAxis: item.dayName,
              total: item.total,
              new: item.new
            }
          });
          setReportExportData(res?.payload?.data?.currentWeekReport)
          setUserGraphData(constructGraphData)
        } else if (type === "monthly") {
          constructGraphData = res?.payload?.data?.monthlyReport?.map((item) => {
            return {
              xAxis: item.month,
              total: item.total,
              new: item.new
            }
          });
          setReportExportData(res?.payload?.data?.monthlyReport)
          setUserGraphData(constructGraphData);
        } else if (type === "yearly") {
          constructGraphData = res?.payload?.data?.yearlyReport?.map((item) => {
            return {
              xAxis: item.year,
              total: item.total,
              new: item.new
            }
          });
          setReportExportData(res?.payload?.data?.yearlyReport)
          setUserGraphData(constructGraphData);
        } else if (type === "custom") {
          let subType = res?.payload?.data.type;
          if (subType === "custom-day") {
            constructGraphData = res?.payload?.data?.customDateReport?.map((item) => {
              return {
                xAxis: item.dateNumber,
                total: item.total,
                new: item.new
              }
            });
            setReportExportData(res?.payload?.data?.customDateReport)
            setUserGraphData(constructGraphData);
          } else if (subType === "custom-month") {
            constructGraphData = res?.payload?.data?.customDateReport?.map((item) => {
              return {
                xAxis: item.month,
                total: item.total,
                new: item.new
              }
            });
            setReportExportData(res?.payload?.data?.customDateReport)
            setUserGraphData(constructGraphData);
          } else if (subType === "custom-year") {
            constructGraphData = res?.payload?.data?.customDateReport?.map((item) => {
              return {
                xAxis: item.year,
                total: item.total,
                new: item.new
              }
            });
            setReportExportData(res?.payload?.data?.customDateReport)
            setUserGraphData(constructGraphData);
          }
        }
      }
    });
  }, [dispatch]);

  const dateRangeCancel = () => {
    setDateRangeOpen(false);
    if (customViewRef !== "custom") setUserGraphView(customViewRef);
  };
  const dateRangeApply = () => {
    getUsersGraphReportDetail(tenant.id, userGraphView, startDate, endDate)
    setCustomViewRef("custom")
  };
  const changeGraphView = (e) => {
    if (e.target.value === "custom") {
      setCustomViewRef(userGraphView);
      setDateRangeOpen(true);
    }
    setUserGraphView(e.target.value);
  };
  useEffect(() => {
    if (!hasFetched.current && tenant?.id) {
      getReportDetail(tenant.id);
      hasFetched.current = true;
    }
  }, [tenant?.id, getReportDetail]);

  useEffect(() => {
    if (tenant?.id && userGraphView) {
      if (userGraphView !== 'custom') getUsersGraphReportDetail(tenant.id, userGraphView);
    }

  }, [tenant?.id, userGraphView, getUsersGraphReportDetail]);

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Grid container spacing={2}>
        {/* Top Stat Cards */}
        {[
          {
            icon: (
              <PeopleAltOutlinedIcon
                sx={{ fill: "#3874CB", height: 40, width: 40 }}
              />
            ),
            title: "Allowed License",
            value: reportDetails?.licenseDetails?.totalLicenses,
            info: "Total active user: ",
            infoValue: reportDetails?.tenantUserAccounts?.total,
            change: `↑ ${reportDetails?.tenantUserAccounts?.last7DaysUsers} `,
            subText: "vs last 7 days",
          },
          {
            icon: (
              <SchoolOutlinedIcon
                sx={{ fill: "#3874CB", height: 40, width: 40 }}
              />
            ),
            title: "Courses",
            value: reportDetails?.courseReport?.totalCourses,
            change: `↑ ${reportDetails?.courseReport?.lastMonthCourses} `,
            subText: "vs last month",
          }
        ].map((card, idx) => (
          <Grid item xs={12} md={4} key={idx}>
            <Card sx={{ textAlign: "left", pl: 2 }}>
              <CardContent sx={{ minHeight: "180px" }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: "100%" }}>
                    <Box justifyContent="space-between" display="flex">
                      {card.icon}
                      {card.info && (
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "16px !important" }}
                        >
                          {card.info}{" "}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              fontSize: "16px !important",
                              fontWeight: 600,
                            }}
                            color="text.primary"
                          >
                            {card.infoValue}
                          </Typography>
                        </Typography>
                      )}
                    </Box>
                    <Typography marginTop="8px" variant="h5" fontWeight="bold">
                      {card.value}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#374151",
                        fontSize: "18px",
                        fontWeight: "500",
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      alignItems="center"
                      fontSize="16px"
                      marginTop="8px"
                      display="flex"
                      gap={1}
                      variant="body2"
                      color="text.primary"
                      fontWeight="bold"
                    >
                      {card.change}
                      <Typography component="span">{card.subText}</Typography>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* User Graphs */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: "18px", fontWeight: "600" }}
                >
                  User Graphs
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  {/* Custom legend */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      width={12}
                      height={12}
                      borderRadius="20%"
                      bgcolor="#90CAF9"
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#2A2F3D",
                        fontWeight: 500,
                        fontSize: "16px",
                      }}
                    >
                      Total Users
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      width={12}
                      height={12}
                      borderRadius="20%"
                      bgcolor="#42A5F5"
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#2A2F3D",
                        fontWeight: 500,
                        fontSize: "16px",
                      }}
                    >
                      New Users
                    </Typography>
                  </Box>
                  {/* Controls */}
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      border="1px solid #ccc"
                      justifyContent="left"
                      borderRadius="4px"
                    >
                      <CalendarMonthOutlinedIcon sx={{ fontSize: 18, ml: 1 }} />
                      <Select
                        variant="standard"
                        disableUnderline
                        value={userGraphView}
                        onChange={changeGraphView}
                        IconComponent={KeyboardArrowDownOutlinedIcon}
                        sx={{
                          padding: "4px 0px",
                          minWidth: 120,
                          margin: "0px 4px",
                          "& .MuiSelect-select": { padding: 0 },
                          "& .MuiSelect-icon": { color: "#1F2A37" },
                        }}
                      >
                        <MenuItem value="current-month">Current Month</MenuItem>
                        <MenuItem value="current-week">Current Week</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="yearly">Yearly</MenuItem>
                        <MenuItem value="custom">Custom</MenuItem>
                      </Select>
                    </Box>
                  </FormControl>
                  {userGraphView === "custom" && <CustomDateRangePopup
                    startDate={startDate} setStartDate={setStartDate}
                    endDate={endDate} setEndDate={setEndDate}
                    onApply={dateRangeApply} open={dateRangeOpen}
                    setOpen={setDateRangeOpen} dateRangeCancel={dateRangeCancel}
                  />}
                  <ExportToExcel buttonDisabled={reportExportData?.length === 0} data={reportExportData} fileName="User_Report.xlsx" />
                </Box>
              </Box>
              {userGraphData && userGraphData?.length > 0 &&
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={userGraphData}>
                    <CartesianGrid strokeDasharray="0 2000" />
                    <XAxis
                      dataKey="xAxis"
                      interval={0}
                      tick={{ fill: "#878787" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#878787" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        fontSize: "12px",
                        padding: "8px 12px",
                      }}
                      itemStyle={{
                        fontSize: "12px",
                      }}
                      labelStyle={{
                        fontWeight: "bold",
                        fontSize: "13px",
                      }} />
                    <Bar
                      dataKey="total"
                      fill="#76A9FA"
                      name="Total Users"
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      dataKey="new"
                      fill="#3874CB"
                      name="New Users"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              }
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Graph */}
        <Grid sx={{ display: "none" }} item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ fontSize: "18px", fontWeight: "600" }}>
                  Monthly/Yearly Subscription Plan
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box width={12} height={12} borderRadius="20%" bgcolor="#1976D2" />
                    <Typography variant="caption" sx={{ color: "#2A2F3D", fontWeight: 500, fontSize: "16px" }}>
                      Amount
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ maxWidth: 220, textAlignLast: 'center' }}>
                    <Box>
                      <TextField
                        required
                        variant="outlined"
                        type="month"
                        fullWidth
                        value={subscriptionView}
                        inputRef={inputRef}
                        onChange={(e) => {
                          setSubscriptionView(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if ((e.key === "Backspace" || e.key === "Delete") && e.target.value) {
                            e.preventDefault();
                          }
                        }}
                        slotProps={{
                          actionBar: { actions: [] }, // removes both 'clear' and 'today'
                        }}
                        sx={{
                          border: "1px solid #ccc",
                          padding: "0",
                          borderRadius: "4px",
                          justifyContent: "left",
                          "& .MuiInputLabel-asterisk": { color: "red" },
                          // Hide the default calendar icon
                          "& input[type='month']::-webkit-calendar-picker-indicator": {
                            opacity: 0,
                            width: 0,
                            margin: 0,
                            pointerEvents: "none",
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "8px"
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <CalendarMonthOutlinedIcon sx={{ pointerEvents: "none" }} />
                          ),
                          endAdornment: (
                            <KeyboardArrowDownOutlinedIcon
                              sx={{ cursor: "pointer" }}
                              onClick={() => {
                                if (inputRef.current) inputRef.current.showPicker?.();
                              }}
                            />
                          ),
                        }}
                      />
                    </Box>
                  </FormControl>
                  <IconButton
                    sx={{
                      border: "1px solid #3874CB",
                      borderRadius: "8px",
                      p: "4px",
                    }}
                  >
                    <SaveAltOutlinedIcon sx={{ fill: "#3874CB" }} />
                  </IconButton>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={subscriptionData}
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    vertical={true}
                    horizontal={false}
                    stroke="#E3E8EF"
                    strokeWidth={1}
                  />
                  <XAxis
                    dataKey="month"
                    interval={0}
                    tick={{ fill: "#878787" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#878787" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    name="Amount"
                    stroke="#1976D2"
                    strokeWidth={3}
                    dot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportDetail;
